import type { FC, ReactNode } from 'react';
import { Button, ConfigProvider, Divider, Space } from 'antd';
import { TbSquareCheckFilled, TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import { SMALL_RESOURCE_FOOTER_MIN_HEIGHT } from '../../layouts/footers/SmallResourceFooter/constants';
import SmallTooltip from '../../tooltips/SmallTooltip';

interface DataGridSelectionToolbarProps {
    bottom?: string;
    children?: ReactNode;
    label: ReactNode;
    loading?: boolean;
    onClear: () => void;
    position?: 'absolute' | 'fixed' | 'static';
}

export const SELECTION_TOOLBAR_CLEARANCE = '1.5rem';
export const FIXED_SELECTION_TOOLBAR_BOTTOM = `calc(${SMALL_RESOURCE_FOOTER_MIN_HEIGHT} + ${SELECTION_TOOLBAR_CLEARANCE})`;

const DataGridSelectionToolbar: FC<DataGridSelectionToolbarProps> = ({
    bottom,
    children,
    label,
    loading = false,
    onClear,
    position = 'absolute',
}) => {
    const { token } = useAntDesignTheme();
    const resolvedBottom =
        position === 'static'
            ? undefined
            : (bottom ?? (position === 'fixed' ? FIXED_SELECTION_TOOLBAR_BOTTOM : SELECTION_TOOLBAR_CLEARANCE));

    return (
        <ConfigProvider componentSize='small'>
            <div
                data-compact-list-selection-control='true'
                role='toolbar'
                aria-label='Data grid selection actions'
                aria-busy={loading}
                style={{
                    alignItems: 'center',
                    background: token.colorBgElevated,
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    bottom: resolvedBottom,
                    boxShadow: token.boxShadowSecondary,
                    display: 'flex',
                    left: position === 'static' ? undefined : '50%',
                    padding: '0.25rem',
                    position,
                    transform: position === 'static' ? undefined : 'translateX(-50%)',
                    zIndex: 20,
                }}
            >
                <Space.Compact style={{ borderRadius: token.borderRadius }}>
                    <Button
                        aria-label={loading ? 'Selection action in progress' : undefined}
                        loading={loading}
                        type='dashed'
                        style={{ alignItems: 'center', display: 'inline-flex', gap: '0.375rem', pointerEvents: 'none' }}
                    >
                        <TbSquareCheckFilled color={token.colorPrimary} size='1rem' />
                        {label}
                    </Button>
                    <SmallTooltip title='Deselect All' padding='0.5rem 0'>
                        <Button
                            aria-label='Deselect all'
                            disabled={loading}
                            type='dashed'
                            icon={<TbX size='1rem' style={{ color: token.colorTextTertiary, display: 'block' }} />}
                            onClick={onClear}
                        />
                    </SmallTooltip>
                </Space.Compact>
                {children ? (
                    <>
                        <Divider type='vertical' style={{ height: '1rem', margin: '0 0.375rem' }} />
                        {children}
                    </>
                ) : null}
            </div>
        </ConfigProvider>
    );
};

export default DataGridSelectionToolbar;
